// AISignalsTab.js
import React, { useState, useEffect, useCallback } from "react";
import { fetchQuote, fetchOverview, fetchTimeSeries, fetchNews } from "../../config/api";
import {
  generateAISignal,
  getCachedAISignal,
  clearAISignalCache,
} from "../../config/aiSignalsApi";
import "./AISignalsTab.css";

// ── RSI calculator (last value only) ─────────
function lastRSI(closes, period = 14) {
  if (closes.length < period + 2) return null;
  let gains = 0, losses = 0;
  for (let i = closes.length - period; i < closes.length; i++) {
    const diff = closes[i] - closes[i - 1];
    if (diff > 0) gains += diff;
    else losses -= diff;
  }
  const rs = gains / (losses || 1);
  return +(100 - 100 / (1 + rs)).toFixed(1);
}

// ── SMA ───────────────────────────────────────
function sma(closes, n) {
  if (closes.length < n) return null;
  return +(closes.slice(-n).reduce((a, b) => a + b, 0) / n).toFixed(2);
}

// ── Trend helper ──────────────────────────────
function trendLabel(closes) {
  if (closes.length < 10) return "Insufficient data";
  const last = closes[closes.length - 1];
  const sma20 = sma(closes, 20);
  const sma50 = sma(closes, Math.min(50, closes.length));
  if (!sma20 || !sma50) return "Unknown";
  if (last > sma20 && sma20 > sma50) return "Strong Uptrend";
  if (last > sma20) return "Mild Uptrend";
  if (last < sma20 && sma20 < sma50) return "Strong Downtrend";
  return "Sideways / Consolidating";
}

// ── Loading steps ─────────────────────────────
const STEPS = [
  "Fetching live quote & fundamentals",
  "Analysing technicals & price action",
  "Reading recent news sentiment",
  "Generating VeeraStockIQ AI Signal",
];

// ── Colour helpers ────────────────────────────
function verdictClass(v = "") {
  const u = v.toUpperCase();
  if (u.includes("BUY")) return "buy";
  if (u.includes("AVOID") || u.includes("SELL")) return "avoid";
  return "hold";
}

function verdictIcon(v = "") {
  const u = v.toUpperCase();
  if (u.includes("BUY")) return "✅";
  if (u.includes("AVOID") || u.includes("SELL")) return "🚫";
  return "⚖";
}

function scoreColor(n) {
  if (n >= 7) return "#22c55e";
  if (n >= 5) return "#f59e0b";
  return "#ef4444";
}

// ── Format cache expiry time ──────────────────
function formatTimeLeft(ms) {
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "expiring soon";
  if (mins === 1) return "1 minute left";
  if (mins <= 60) return `${mins} minutes left`;
  return "expires in 1 hour";
}

// ─────────────────────────────────────────────
export default function AISignalsTab({ symbol, exchange }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stepIdx, setStepIdx] = useState(-1);
  const [error, setError] = useState(null);
  const [timestamp, setTimestamp] = useState(null);
  const [isCached, setIsCached] = useState(false);
  const [cacheExpiresIn, setCacheExpiresIn] = useState(null);

  // Pre-fetch data state (collected before AI call)
  const [ctx, setCtx] = useState(null);

  // Load cache on mount or when symbol changes
  useEffect(() => {
    const cached = getCachedAISignal(symbol);
    if (cached) {
      setResult(cached.result);
      setTimestamp(new Date(cached.timestamp));
      setIsCached(true);
      setCacheExpiresIn(cached.expiresIn);

      // Update expiry countdown every minute
      const interval = setInterval(() => {
        setCacheExpiresIn((prev) => {
          if (!prev || prev <= 0) {
            clearInterval(interval);
            return null;
          }
          return prev - 60000;
        });
      }, 60000);

      return () => clearInterval(interval);
    } else {
      setResult(null);
      setIsCached(false);
    }
  }, [symbol]);

  // Pre-fetch market context on mount for fast generation
  useEffect(() => {
    let cancelled = false;
    async function prefetch() {
      try {
        const [quote, overview] = await Promise.allSettled([
          fetchQuote(symbol, exchange),
          fetchOverview(symbol, exchange),
        ]);
        if (!cancelled) {
          setCtx({
            quote: quote.status === "fulfilled" ? quote.value : null,
            overview: overview.status === "fulfilled" ? overview.value : null,
          });
        }
      } catch (_) {}
    }
    prefetch();
    return () => {
      cancelled = true;
    };
  }, [symbol, exchange]);

  const generate = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setIsCached(false);
    setCacheExpiresIn(null);
    setStepIdx(0);

    try {
      // ── Step 0: quote + overview (may already be cached) ──
      const [quoteRes, overviewRes] = await Promise.allSettled([
        fetchQuote(symbol, exchange),
        fetchOverview(symbol, exchange),
      ]);
      const quote =
        quoteRes.status === "fulfilled" ? quoteRes.value : ctx?.quote || {};
      const overview =
        overviewRes.status === "fulfilled"
          ? overviewRes.value
          : ctx?.overview || {};

      setStepIdx(1);

      // ── Step 1: technicals (3M daily candles) ──
      let rsi = null,
        trend = "Unknown",
        sma20val = null,
        sma50val = null;
      try {
        const ts = await fetchTimeSeries(symbol, exchange, "3M");
        const candles = ts?.time_series || [];
        const closes = candles.map((c) => c.close).filter(Boolean);
        rsi = lastRSI(closes);
        trend = trendLabel(closes);
        sma20val = sma(closes, 20);
        sma50val = sma(closes, Math.min(50, closes.length));
      } catch (_) {}

      setStepIdx(2);

      // ── Step 2: news headlines ──
      let headlines = [];
      try {
        const newsData = await fetchNews(symbol, exchange);
        headlines = (newsData?.news || []).slice(0, 6).map((n) => n.title);
      } catch (_) {}

      setStepIdx(3);

      // ── Step 3: prepare payload & call VeeraStockIQ AI ──
      const price = quote.price || overview.price || 0;
      const changePercent = quote.change_percent || 0;

      const payload = {
        symbol,
        price,
        changePercent,
        trend,
        rsi,
        sma20: sma20val,
        sma50: sma50val,
        volume: quote.volume,
        peRatio: overview.company_pe_ratio,
        marketCap: overview.company_market_cap,
        shareholding: overview.shareholding,
        news: headlines,
      };

      const aiResult = await generateAISignal(payload);

      setResult(aiResult);
      setTimestamp(new Date());
      setIsCached(false);

      // Set initial expiry time
      const oneHourMs = 60 * 60 * 1000;
      setCacheExpiresIn(oneHourMs);

      // Update expiry countdown every minute
      const interval = setInterval(() => {
        setCacheExpiresIn((prev) => {
          if (!prev || prev <= 0) {
            clearInterval(interval);
            return null;
          }
          return prev - 60000;
        });
      }, 60000);

      return () => clearInterval(interval);
    } catch (e) {
      setError(
        e.message?.includes("JSON")
          ? "Analysis returned an unexpected format. Please try again."
          : `Analysis failed: ${e.message}`
      );
      setStepIdx(-1);
    } finally {
      setLoading(false);
    }
  }, [symbol, exchange, ctx]);

  const handleClearCache = () => {
    clearAISignalCache(symbol);
    setResult(null);
    setTimestamp(null);
    setIsCached(false);
    setCacheExpiresIn(null);
  };

  // ── Render: loading ───────────────────────
  if (loading) {
    return (
      <div className="ai-wrap">
        <div className="ai-loading">
          <div className="ai-loading-brain">🧠</div>
          <div className="ai-loading-label">
            ANALYSING {symbol} · {exchange}
          </div>
          <div className="ai-loading-steps">
            {STEPS.map((s, i) => (
              <div
                key={i}
                className={`ai-loading-step ${
                  i < stepIdx ? "done" : i === stepIdx ? "active" : ""
                }`}
              >
                <span className="ai-step-dot" />
                {i < stepIdx ? "✓ " : ""}
                {s}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Render: no result yet ─────────────────
  if (!result) {
    return (
      <div className="ai-wrap">
        <div className="ai-cta-card">
          <div className="ai-cta-glow" />
          <div className="ai-cta-icon">🤖</div>
          <h2 className="ai-cta-title">AI Entry &amp; Exit Signals</h2>
          <p className="ai-cta-sub">
            VeeraStockIQ AI will analyse live price data, fundamentals, technical indicators,
            and recent news for{" "}
            <strong style={{ color: "#94a3b8" }}>{symbol}</strong> to generate a precise trade
            recommendation with entry, target, and stop-loss levels.
          </p>
          <div className="ai-cta-features">
            {[
              "Entry Price",
              "Target Price",
              "Stop Loss",
              "R:R Ratio",
              "AI Score /10",
              "BUY / HOLD / AVOID",
            ].map((f) => (
              <span key={f} className="ai-cta-chip">
                {f}
              </span>
            ))}
          </div>
          {error && (
            <div className="ai-error" style={{ textAlign: "left", marginTop: "0.5rem" }}>
              ⚠ {error}
            </div>
          )}
          <button className="ai-gen-btn" onClick={generate}>
            <span>⚡</span> Generate AI Analysis
          </button>
          <p style={{ fontSize: "0.68rem", color: "#2d3f59", margin: 0 }}>
            On-demand · Uses VeeraStockIQ AI · ~5–8 seconds
          </p>
        </div>
      </div>
    );
  }

  // ── Render: result ────────────────────────
  const vc = verdictClass(result.verdict);
  const vi = verdictIcon(result.verdict);
  const cmp = ctx?.quote?.price || 0;

  const upside =
    result.target_price && cmp
      ? (((result.target_price - cmp) / cmp) * 100).toFixed(1)
      : null;
  const downside =
    result.stop_loss && cmp
      ? (((cmp - result.stop_loss) / cmp) * 100).toFixed(1)
      : null;

  return (
    <div className="ai-wrap">
      {/* ── Cache indicator ── */}
      {isCached && cacheExpiresIn && (
        <div className="ai-cache-banner">
          <span className="ai-cache-icon">💾</span>
          <span className="ai-cache-text">
            Cached from{" "}
            {timestamp?.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
            })}{" "}
            · {formatTimeLeft(cacheExpiresIn)}
          </span>
          <button className="ai-cache-clear" onClick={handleClearCache} title="Clear cache">
            ✕
          </button>
        </div>
      )}

      {/* ── Verdict banner ── */}
      <div className={`ai-verdict-banner ${vc}`}>
        <span className="ai-verdict-icon">{vi}</span>
        <div className="ai-verdict-text">
          <div className="ai-verdict-label">AI Verdict — {result.timeframe || "Medium-term"}</div>
          <div className="ai-verdict-val">{result.verdict}</div>
        </div>
        <div className="ai-score-ring">
          <span className="ai-score-num" style={{ color: scoreColor(result.composite_score) }}>
            {result.composite_score}
            <span style={{ fontSize: "1rem", color: "#334155" }}>/10</span>
          </span>
          <span className="ai-score-label">AI Score</span>
        </div>
      </div>

      {/* ── Price targets ── */}
      <div>
        <div className="ai-section-label">Price Targets</div>
        <div className="ai-targets-grid">
          <div className="ai-target-card entry">
            <div className="ai-target-label">Entry Price</div>
            <div className="ai-target-val">
              ₹
              {Number(result.entry_price).toLocaleString("en-IN", {
                maximumFractionDigits: 2,
              })}
            </div>
            <div className="ai-target-sub">
              {cmp && result.entry_price
                ? cmp <= result.entry_price
                  ? "At or above entry — consider waiting"
                  : `${(((cmp - result.entry_price) / cmp) * 100).toFixed(1)}% below CMP`
                : "Ideal buy zone"}
            </div>
          </div>

          <div className="ai-target-card target">
            <div className="ai-target-label">Target Price</div>
            <div className="ai-target-val">
              ₹
              {Number(result.target_price).toLocaleString("en-IN", {
                maximumFractionDigits: 2,
              })}
            </div>
            <div className="ai-target-sub">
              {upside ? `+${upside}% upside from CMP` : "Price target"}
            </div>
          </div>

          <div className="ai-target-card sl">
            <div className="ai-target-label">Stop Loss</div>
            <div className="ai-target-val">
              ₹
              {Number(result.stop_loss).toLocaleString("en-IN", {
                maximumFractionDigits: 2,
              })}
            </div>
            <div className="ai-target-sub">
              {downside ? `−${downside}% from CMP` : "Below key support"}
            </div>
          </div>

          <div className="ai-target-card rr">
            <div className="ai-target-label">Risk / Reward</div>
            <div className="ai-target-val">{result.rr_ratio || "—"}</div>
            <div className="ai-target-sub">Reward-to-risk ratio</div>
          </div>
        </div>
      </div>

      {/* ── Sub-scores ── */}
      <div>
        <div className="ai-section-label">Score Breakdown</div>
        <div className="ai-scores-row">
          {[
            { label: "Fundamental", score: result.fundamental_score },
            { label: "Technical", score: result.technical_score },
            { label: "News Sentiment", score: result.sentiment_score },
          ].map(({ label, score }) => (
            <div className="ai-score-card" key={label}>
              <div className="ai-score-bar-wrap">
                <div className="ai-score-card-label">{label}</div>
                <div className="ai-score-track">
                  <div
                    className="ai-score-fill"
                    style={{
                      width: `${(score / 10) * 100}%`,
                      background: scoreColor(score),
                    }}
                  />
                </div>
              </div>
              <div className="ai-score-card-num" style={{ color: scoreColor(score) }}>
                {score}
                <span style={{ fontSize: "0.7rem", color: "#334155" }}>/10</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Reasoning ── */}
      <div className="ai-reasoning-card">
        <div className="ai-reasoning-header">
          <span>🧠</span> AI Reasoning
        </div>
        {result.reasoning && <p className="ai-reasoning-text">{result.reasoning}</p>}
        {result.key_points?.length > 0 && (
          <ul className="ai-reasoning-points">
            {result.key_points.map((pt, i) => (
              <li key={i} className="ai-reasoning-point">
                <span className="ai-point-dot" />
                {pt}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="ai-meta-row">
        <span className="ai-timestamp">
          Generated{" "}
          {timestamp?.toLocaleString("en-IN", {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })}
          {isCached && " (cached)"}
        </span>
        <button
          className="ai-gen-btn regenerate"
          onClick={generate}
          disabled={loading}
        >
          ↻ Regenerate
        </button>
        <span className="ai-disclaimer">Not financial advice · For research purposes only</span>
      </div>
    </div>
  );
}